import { z } from 'zod';
import { withVersionTest } from '../shared/json-patch.ts';
import type { JsonPatch } from '../shared/json-patch.ts';
import { toolDefinitions } from '../shared/tool-definitions.ts';
import { queryClient } from './api.ts';

type ToolAnnotations = {
  readOnlyHint?: boolean;
  untrustedContentHint?: boolean;
  consequentialHint?: boolean;
};
type ModelContextTool = {
  name: string;
  title?: string;
  description: string;
  inputSchema?: object;
  annotations?: ToolAnnotations;
  // The second argument is not reliably passed: Chrome DevTools’ “run tool” calls execute(input) alone.
  execute: (input: object, options?: { signal?: AbortSignal }) => unknown;
};
type RegisteredTool = {
  name: string;
  title?: string;
  description: string;
  inputSchema?: object;
  window: Window;
  origin: string;
  annotations?: ToolAnnotations;
};
interface ModelContext extends EventTarget {
  registerTool(
    tool: ModelContextTool,
    options?: { signal?: AbortSignal; exposedTo?: string[] },
  ): Promise<undefined>;
  getTools(options?: { fromOrigins?: string[] }): Promise<RegisteredTool[]>;
  executeTool(
    tool: RegisteredTool,
    inputObject?: unknown,
    options?: { signal?: AbortSignal },
  ): Promise<string>;
  ontoolchange: ((this: ModelContext, event: Event) => unknown) | null;
}
declare global {
  interface Document {
    readonly modelContext?: ModelContext;
  }
}
export async function registerWebMcp(): Promise<void> {
  const context = document.modelContext;
  if (!context) return;
  for (const definition of Object.values(toolDefinitions).sort((a, b) => a.name.localeCompare(b.name)))
    await context.registerTool({
      name: definition.name,
      description: definition.description,
      inputSchema: z.toJSONSchema(definition.schema),
      annotations: { readOnlyHint: definition.readOnly },
      execute: async (input, options) => {
        const args = input as Record<string, unknown>;
        let request: ApiRequest;
        let contentType = 'application/json';
        switch (definition.name) {
          case 'api_request': {
            request = args as ApiRequest;
            const target = new URL(request.path, location.origin);
            if (
              target.origin !== location.origin ||
              !target.pathname.startsWith('/api/') ||
              target.search ||
              target.hash
            )
              throw new Error('Use a Wirechunk API path and supply query parameters separately.');
            break;
          }
          case 'patch_page': {
            const { pageId, expectedVersionId, patch } = definition.schema.parse(input);
            request = {
              method: 'PATCH',
              path: `/api/pages/${pageId}`,
              body: withVersionTest(expectedVersionId, patch),
            };
            contentType = 'application/json-patch+json';
            break;
          }
          case 'patch_site_settings': {
            const { siteId, expectedVersionId, patch } = definition.schema.parse(input);
            request = {
              method: 'PATCH',
              path: `/api/sites/${siteId}/settings`,
              body: withVersionTest(expectedVersionId, patch),
            };
            contentType = 'application/json-patch+json';
            break;
          }
          case 'patch_form': {
            const { formId, expectedVersionId, patch } = definition.schema.parse(input);
            request = {
              method: 'PATCH',
              path: `/api/forms/${formId}`,
              body: withVersionTest(expectedVersionId, patch),
            };
            contentType = 'application/json-patch+json';
            break;
          }
          case 'patch_form_submission_action': {
            const { id, expectedVersionId, patch } = definition.schema.parse(input);
            request = {
              method: 'PATCH',
              path: `/api/form-submission-actions/${id}`,
              body: withVersionTest(expectedVersionId, patch),
            };
            contentType = 'application/json-patch+json';
            break;
          }
          case 'patch_asset': {
            const { assetId, expectedVersionId, patch } = definition.schema.parse(input);
            request = {
              method: 'PATCH',
              path: `/api/assets/${assetId}`,
              body: withVersionTest(expectedVersionId, patch),
            };
            contentType = 'application/json-patch+json';
            break;
          }
          case 'generate_color_scale':
            request = { method: 'POST', path: '/api/color-scales', body: args };
            break;
          case 'set_site_color_palette':
            request = {
              method: 'POST',
              path: `/api/sites/${encodeURIComponent(String(args.siteId))}/color-palettes`,
              body: { palette: args.palette, baseColor: args.baseColor },
            };
            break;
          case 'search_icons':
            request = { method: 'GET', path: '/api/icons', query: args };
            break;
          case 'send_product_feedback':
            request = { method: 'POST', path: '/api/product-feedback', body: args };
            break;
          case 'validate_site':
            request = {
              method: 'GET',
              path: `/api/sites/${encodeURIComponent(String(args.siteId))}/validation`,
            };
            break;
          case 'import_asset': {
            const { siteId, ...body } = args;
            request = {
              method: 'POST',
              path: `/api/sites/${encodeURIComponent(String(siteId))}/assets-import`,
              body,
            };
            break;
          }
          case 'read_openapi_schema':
            request = { method: 'GET', path: '/api/web-mcp/read-openapi-schema', query: args };
            break;
          case 'search_openapi_schema':
            request = { method: 'GET', path: '/api/web-mcp/search-openapi-schema', query: args };
            break;
          case 'activate_skill':
            request = { method: 'GET', path: '/api/web-mcp/activate-skill', query: args };
            break;
        }
        const url = new URL(request.path, location.origin);
        for (const [key, value] of Object.entries(request.query ?? {}))
          if (value !== undefined && value !== null)
            url.searchParams.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        const response = await fetch(url, {
          method: request.method,
          headers: request.body ? { 'Content-Type': contentType } : undefined,
          credentials: 'same-origin',
          body: request.body ? JSON.stringify(request.body) : undefined,
          signal: options?.signal,
        });
        const result: unknown = response.headers.get('content-type')?.includes('json')
          ? await response.json()
          : await response.text();
        if (response.ok && !definition.readOnly) await queryClient.invalidateQueries();
        return result;
      },
    });
}

type ApiRequest = {
  method: string;
  path: string;
  query?: Record<string, unknown>;
  body?: Record<string, unknown> | JsonPatch;
};
